// src/components/desk/contract/AmountInput.jsx — a money box that GROUPS THOUSANDS while you type
// ------------------------------------------------------------------------------------------
// Owner 2026-09-11, looking at a binder's "150000": "let's separate thousands in all forms."
// A price without separators is read wrong — 150000 and 15000 differ by one glyph nobody
// counts — and every figure in a contract is a figure somebody will be held to.
//
// ⚠️ `<input type="number">` CANNOT show a separator: the browser refuses any value its own
// number parser rejects, so "150,000" is dropped on the floor and the field goes blank. So this
// is a TEXT input with `inputMode="decimal"` (the phone still gets the numeric keypad) that
// formats what it shows and hands the CALLER a plain number — the stored value never contains a
// comma, which is what the API and every sum downstream expect.
//
// Three details that look fussy and are not:
//   • THE CARET IS RESTORED BY DIGIT COUNT, not by character offset. Inserting a digit can add a
//     separator before the caret, so a naive "keep the offset" jumps the cursor one place left
//     every few keystrokes; counting digits before the caret and finding that digit again in the
//     formatted text puts it back where the typist expects.
//   • WHILE FOCUSED THE FIELD OWNS ITS TEXT. A half-typed "1500." or "1,500.0" is not yet a
//     number the formatter should rewrite, and re-deriving the text from the value on every
//     keystroke would eat the trailing dot and the trailing zero. The text is re-derived from
//     the value only when the box is not focused (an external change: a re-seeded draft, the
//     paid↔remaining link).
//   • ONE FORMATTER, `fmtNum` from prepaySentence.js — the same one the contract's own sentence
//     uses, so the box and the document group and round identically.
import { useEffect, useRef, useState } from 'react'
import { fmtNum } from '../../../lib/prepaySentence.js'

// "1,500.50" / "1500,5" / " 1 500 " → "1500.5"; anything else → ''. Accepts a comma as the
// decimal mark (a Georgian or Russian keyboard gives one) when there is no dot.
export function parseAmount(text) {
  let s = String(text ?? '').trim()
  if (!s) return ''
  if (!s.includes('.') && (s.match(/,/g) || []).length === 1 && /,\d{1,2}$/.test(s)) s = s.replace(',', '.')
  s = s.replace(/[^\d.]/g, '')
  const dot = s.indexOf('.')
  if (dot >= 0) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '')
  return s
}
// "1500.5" → "1,500.5"; keeps a trailing dot and up to two decimals as typed.
export function groupAmount(clean) {
  if (clean === '' || clean === '.') return clean
  const [int, dec] = clean.split('.')
  const grouped = (int || '').replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return dec === undefined ? grouped : `${grouped}.${dec.slice(0, 2)}`
}

export function AmountInput({ value, onChange, className = '', disabled = false, ...rest }) {
  const ref = useRef(null)
  const focused = useRef(false)
  const [text, setText] = useState(() => (value == null || value === '' ? '' : fmtNum(value)))

  useEffect(() => {
    if (focused.current) return
    setText(value == null || value === '' ? '' : fmtNum(value))
  }, [value])

  const handle = e => {
    const el = e.target
    const raw = el.value
    const caret = el.selectionStart ?? raw.length
    const digitsBefore = raw.slice(0, caret).replace(/\D/g, '').length
    const clean = parseAmount(raw)
    const next = groupAmount(clean)
    setText(next)
    onChange(clean === '' || clean === '.' ? null : Number(clean))
    // put the caret back after the same DIGIT it was after (see the header).
    const put = () => {
      const node = ref.current
      if (!node || document.activeElement !== node) return
      let pos = 0
      if (digitsBefore > 0) { let seen = 0; pos = next.length; for (let k = 0; k < next.length; k++) { if (/\d/.test(next[k])) { seen++; if (seen === digitsBefore) { pos = k + 1; break } } } }
      try { node.setSelectionRange(pos, pos) } catch { /* a detached node */ }
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(put); else setTimeout(put, 0)
  }

  return (
    <input ref={ref} type="text" inputMode="decimal" autoComplete="off" className={className} disabled={disabled}
      value={text} onChange={handle}
      onFocus={() => { focused.current = true }}
      onBlur={() => { focused.current = false; setText(value == null || value === '' ? '' : fmtNum(value)) }}
      {...rest} />
  )
}
